// ConnDbPickerModal — pick a connection + database (scoped to what the caller
// can access) before opening the backup or restore dialog from the Backup /
// Restore tab. For a restore target, allowNew offers "create a new database".
//
// Props:
//   title         string  — modal heading
//   confirmLabel  string  — primary button label (default "Continue")
//   allowNew      bool    — offer a "new database" option (restore target only)
//   onPick(sel)   sel = { connectionId, connectionName, database, createDb }
//   onClose()
import { createSignal, createResource, createEffect, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { api } from '../lib/api.js';

export default function ConnDbPickerModal(props) {
  const [connectionId, setConnectionId] = createSignal(0);
  const [mode, setMode] = createSignal('existing'); // 'existing' | 'new'
  const [existingDb, setExistingDb] = createSignal('');
  const [newDb, setNewDb] = createSignal('');

  const [connections] = createResource(async () => {
    try { const r = await api.get('/api/connections'); return r?.connections || []; }
    catch { return []; }
  });

  // Databases for the chosen connection — reloads when the connection changes.
  // The endpoint returns { databases: [{name,…}] }, so unwrap to the array.
  const [databases] = createResource(connectionId, async (id) => {
    if (!id) return [];
    try { const r = await api.get(`/api/connections/${id}/databases`); return r?.databases || []; }
    catch { return []; }
  });

  // Convenience: when the chosen connection has exactly one database, pick it
  // automatically so the operator doesn't have to open a one-item dropdown.
  createEffect(() => {
    const dbs = databases();
    if (Array.isArray(dbs) && dbs.length === 1 && !existingDb()) {
      setExistingDb(dbs[0].name);
    }
  });

  const connName = () => (connections() || []).find((c) => c.id === connectionId())?.name || '';
  const targetDb = () => (mode() === 'new' ? newDb().trim() : existingDb());
  const canConfirm = () => connectionId() > 0 && !!targetDb();

  const confirm = () => {
    if (!canConfirm()) return;
    props.onPick?.({
      connectionId: connectionId(),
      connectionName: connName(),
      database: targetDb(),
      createDb: props.allowNew && mode() === 'new',
    });
  };

  return (
    <Modal open={true} title={props.title || 'Choose connection & database'} onClose={props.onClose}>
      <div class="restore-dialog">
        <label class="restore-field">
          <span class="restore-field-label">Connection</span>
          <select class="input" value={connectionId()}
                  onChange={(e) => { setConnectionId(Number(e.currentTarget.value)); setExistingDb(''); }}>
            <option value="0" disabled>Select a connection…</option>
            <For each={connections() || []}>
              {(c) => <option value={c.id}>{c.name}</option>}
            </For>
          </select>
          <Show when={connections() && connections().length === 0}>
            <span class="muted" style="font-size:11px;">No connections available to you.</span>
          </Show>
        </label>

        <Show when={connectionId() > 0}>
          {/* Existing-database picker. */}
          <div class="restore-field">
            <span class="restore-field-label">Database</span>
            <Show when={props.allowNew}>
              <label class="restore-radio" style="margin-bottom:4px;">
                <input type="radio" name="cdpmode" checked={mode() === 'existing'} onChange={() => setMode('existing')} />
                <span>Existing database</span>
              </label>
            </Show>
            <select class="input"
                    disabled={props.allowNew && mode() !== 'existing'}
                    value={existingDb()}
                    onChange={(e) => setExistingDb(e.currentTarget.value)}>
              <option value="" disabled>Select a database…</option>
              <For each={databases() || []}>
                {(d) => <option value={d.name}>{d.name}</option>}
              </For>
            </select>
          </div>

          <Show when={props.allowNew}>
            <div class="restore-field">
              <label class="restore-radio">
                <input type="radio" name="cdpmode" checked={mode() === 'new'} onChange={() => setMode('new')} />
                <span>Create new database</span>
              </label>
              <input class="input" placeholder="new_database_name"
                     disabled={mode() !== 'new'}
                     value={newDb()} onInput={(e) => setNewDb(e.currentTarget.value)} />
            </div>
          </Show>
        </Show>

        <div class="restore-actions">
          <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" onClick={confirm} disabled={!canConfirm()}>
            {props.confirmLabel || 'Continue'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
